import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { MessageCircle, Send } from "lucide-react";
import { fmtTimeAgo } from "../lib/format";
import { toast } from "sonner";

export default function CoordinatorChat({ legId, canPost }) {
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const scrollRef = useRef(null);

    async function load() {
        const { data } = await api.get(`/transport/legs/${legId}/messages`);
        setMessages(data);
    }
    useEffect(() => { if (legId) load(); }, [legId]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages.length]);

    async function submit() {
        if (!user) return toast.error("Sign in to post");
        if (!text.trim()) return;
        setBusy(true);
        try {
            await api.post(`/transport/legs/${legId}/messages`, { content: text.trim() });
            setText("");
            load();
        } catch { toast.error("Could not send"); }
        setBusy(false);
    }

    return (
        <div className="card-warm rounded-2xl overflow-hidden" data-testid="coordinator-chat">
            <div className="px-5 py-3 border-b border-[var(--rl-border)] flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-[var(--rl-primary)]" />
                <div className="font-semibold text-sm">Coordinator thread</div>
                <span className="ml-auto text-[10px] uppercase tracking-widest text-[var(--rl-text-3)]">{messages.length} message{messages.length === 1 ? "" : "s"}</span>
            </div>
            <div ref={scrollRef} className="max-h-[380px] overflow-y-auto p-5 space-y-3 bg-[var(--rl-bg)]" data-testid="chat-messages">
                {messages.length === 0 ? (
                    <div className="text-sm text-[var(--rl-text-3)] text-center py-6">No messages yet. Coordinators and seat-holders can post logistics here.</div>
                ) : messages.map((m) => (
                    <div key={m.id} className={`flex gap-3 ${m.user_id === user?.id ? "flex-row-reverse text-right" : ""}`}>
                        <div className="w-8 h-8 rounded-full bg-[var(--rl-sage)] flex items-center justify-center text-xs font-semibold shrink-0">{m.user_name[0]}</div>
                        <div className={`max-w-[75%] ${m.user_id === user?.id ? "items-end" : ""}`}>
                            <div className="text-xs text-[var(--rl-text-3)] mb-1">{m.user_name} · {fmtTimeAgo(m.created_at)}</div>
                            <div className={`rounded-2xl px-3 py-2 text-sm inline-block ${m.user_id === user?.id ? "bg-[var(--rl-primary)] text-white" : "bg-white border border-[var(--rl-border)]"}`}>
                                {m.content}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {canPost ? (
                <div className="p-4 border-t border-[var(--rl-border)] bg-white">
                    <Textarea value={text} onChange={(e) => setText(e.target.value)}
                              placeholder="Share pickup logistics, updates, questions…"
                              className="border-[var(--rl-border)] min-h-[60px]" data-testid="chat-input" />
                    <div className="flex justify-end mt-2">
                        <Button onClick={submit} disabled={busy || !text.trim()} className="btn-primary rounded-full h-9" data-testid="chat-send">
                            <Send className="w-3.5 h-3.5 mr-1.5" /> Send
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="p-4 border-t border-[var(--rl-border)] bg-[var(--rl-cream)] text-xs text-[var(--rl-text-2)] text-center">
                    Claim a seat on this leg to join the coordinator thread.
                </div>
            )}
        </div>
    );
}
